import { Link } from 'react-router';
import CheckIcon from '@/assets/icons/check.svg?react';
import { PLAN_FACTS } from '@/shared/config/plans';
import { ROUTES } from '@/shared/config/routes';
import { formatCount, formatMoney, formatMonthDay } from '@/shared/lib/format';
import { BadgeIcon } from '@/shared/ui/badge-icon';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import type { PaidPlanId, WorkspaceData } from '../types';
import { SummaryList } from './summary-list';

type CheckoutSuccessCardProps = { plan: PaidPlanId; workspace: WorkspaceData };

export function CheckoutSuccessCard({ plan, workspace }: CheckoutSuccessCardProps) {
  const facts = PLAN_FACTS[plan];
  const subscription = workspace.billing.subscription;
  const next = subscription?.nextBilledAt
    ? formatMonthDay(subscription.nextBilledAt)
    : 'the next period';
  const price = formatMoney(
    subscription?.priceCents ?? facts.priceCents,
    subscription?.currency ?? 'USD',
  );
  const rows = [
    {
      key: 'included',
      label: 'Included from today',
      value: `${formatCount(facts.includedPodHours)} Pod-hours a month`,
      isTotal: false,
    },
    { key: 'pods', label: 'Pods at a time', value: `Up to ${facts.podsAtATime}`, isTotal: false },
    { key: 'next', label: 'Next charge', value: `${price} plus tax on ${next}`, isTotal: false },
  ];
  return (
    <Card asChild>
      <section
        data-enter=""
        aria-labelledby="done-h"
        className="mx-auto mt-6 flex max-w-160 shrink grow basis-full flex-col gap-5 p-8"
      >
        <BadgeIcon tone="ok">
          <CheckIcon className="size-5.5" />
        </BadgeIcon>
        <div className="flex flex-col gap-2">
          <h1 id="done-h" className="font-semibold text-success">
            {facts.name} is active
          </h1>
          <p className="text-muted">
            Your next sessions get Pods right away. Paddle sent the receipt to{' '}
            {workspace.billing.billingEmail}.
          </p>
        </div>
        <SummaryList rows={rows} />
        <div className="flex flex-wrap gap-2.5">
          <Button asChild variant="primary" size="lg">
            <Link to={ROUTES.OVERVIEW}>Go to Overview</Link>
          </Button>
          <Button asChild size="lg">
            <Link to={ROUTES.BILLING}>See billing</Link>
          </Button>
        </div>
      </section>
    </Card>
  );
}
